import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label } from '@/components/ui'
import { Link2, Copy, Check, Loader2, Trash2, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'

interface ShortenedUrl {
  original: string
  short: string
}

export function UrlShortener() {
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [history, setHistory] = useState<ShortenedUrl[]>([])
  const [copied, setCopied] = useState<string | null>(null)

  const shortenUrl = async () => {
    if (!url.trim()) {
      toast.error('Please enter a URL')
      return
    }

    // Basic URL validation
    let urlToShorten = url.trim()
    if (!urlToShorten.startsWith('http://') && !urlToShorten.startsWith('https://')) {
      urlToShorten = 'https://' + urlToShorten
    }

    setLoading(true)
    try {
      const response = await fetch(`https://tinyurl.com/api-create.php?url=${encodeURIComponent(urlToShorten)}`)
      const shortUrl = await response.text()

      if (shortUrl.startsWith('https://tinyurl.com/')) {
        const newEntry: ShortenedUrl = {
          original: urlToShorten,
          short: shortUrl
        }
        setHistory(prev => [newEntry, ...prev])
        setUrl('')
        toast.success('URL shortened successfully')
      } else {
        toast.error('Failed to shorten URL')
      }
    } catch {
      toast.error('Failed to shorten URL')
    } finally {
      setLoading(false)
    }
  }

  const copyUrl = async (shortUrl: string) => {
    try {
      await navigator.clipboard.writeText(shortUrl)
      setCopied(shortUrl)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(null), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const clearHistory = () => {
    setHistory([])
    toast.success('History cleared')
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      shortenUrl()
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Link2 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">URL Shortener</h1>
            <p className="text-muted-foreground">Shorten long URLs using TinyURL</p>
          </div>
        </div>
      </div>

      <Card className="glass">
        <CardHeader>
          <CardTitle>Shorten URL</CardTitle>
          <CardDescription>Enter a long URL to create a short link</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>URL</Label>
            <div className="flex gap-2">
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="https://example.com/very/long/url/here"
                className="flex-1"
              />
              <Button onClick={shortenUrl} disabled={loading}>
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Shorten'
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {history.length > 0 && (
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Shortened URLs</CardTitle>
                <CardDescription>{history.length} URL(s) shortened</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={clearHistory}>
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {history.map((item, index) => (
                <div
                  key={index}
                  className="p-4 rounded-lg bg-muted/30 border border-border space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Original:</span>
                    <p className="text-sm truncate flex-1">{item.original}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Short:</span>
                    <a
                      href={item.short}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1 flex-1"
                    >
                      {item.short}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyUrl(item.short)}
                    >
                      {copied === item.short ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
